/**
 * PATH: packages/ui/src/kanban/components/lane/useLaneRename.ts
 * FILE_ID: UI-KANBAN-LANE-RENAME-HOOK-001
 * PURPOSE: In-place lane rename draft, focus, and commit.
 * ROLE IN ARCHITECTURE: State hook extracted from useKanbanLane.
 * MAIN EXPORTS: useLaneRename
 * NON-RESPONSIBILITIES: Does not render the title input.
 */
import { useEffect, useRef, useState } from 'react'

import type { Lane } from '../../types'

export function useLaneRename(
  lane: Lane,
  onRename?: (lane: Lane, nextName: string) => Promise<unknown> | void,
) {
  const [renaming, setRenaming] = useState(false)
  const [draftName, setDraftName] = useState(lane.name)
  const renameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!renaming) setDraftName(lane.name)
  }, [lane.name, renaming])

  useEffect(() => {
    if (renaming && renameRef.current) {
      renameRef.current.focus()
      renameRef.current.select()
    }
  }, [renaming])

  const commitRename = async () => {
    const next = draftName.trim()
    if (!next || next === lane.name) {
      setRenaming(false)
      setDraftName(lane.name)
      return
    }
    try {
      await onRename?.(lane, next)
    } finally {
      setRenaming(false)
    }
  }

  return {
    renaming,
    setRenaming,
    draftName,
    setDraftName,
    renameRef,
    commitRename,
  }
}
